/* eslint-disable complexity */
/* eslint-disable max-statements */
import { useRef, useMemo, useState } from 'react';
import type { useGetFlags } from './useGetFlags.js';
type FirstSegment =
  'month-input' | 'hour-input' | 'date-picker-icon' | 'time-picker-icon' | 'datetime-picker-icon' | '';
type LastSegment =
  'year-input' | 'meridiem-input' | 'clear-btn' | 'date-picker-icon' | 'time-picker-icon' | 'datetime-picker-icon' | '';
export const useGetReferences = ({
  isWithDateInputs,
  isWithTimeInputs,
  isDateSelector,
  isTimeSelector,
  isDateTimeSelector,
  withAnyPicker,
  withClearBtn,
}: ReturnType<typeof useGetFlags>) => {
  // those 2 states are needed for focus tracking
  const [firstSegment, setFirstSegment] = useState<FirstSegment>('');
  const [lastSegment, setLastSegment] = useState<LastSegment>('');
  // refs are used for "auto-advance" feature
  const monthInputRef = useRef<HTMLInputElement>(null) as React.MutableRefObject<HTMLInputElement>;
  const dayInputRef = useRef<HTMLInputElement>(null) as React.MutableRefObject<HTMLInputElement>;
  const yearInputRef = useRef<HTMLInputElement>(null) as React.MutableRefObject<HTMLInputElement>;
  const hourInputRef = useRef<HTMLInputElement>(null) as React.MutableRefObject<HTMLInputElement>;
  const minutesInputRef = useRef<HTMLInputElement>(null) as React.MutableRefObject<HTMLInputElement>;
  const meridiemInputRef = useRef<HTMLInputElement>(null) as React.MutableRefObject<HTMLInputElement>;
  const clearButtonRef = useRef<HTMLButtonElement>(null) as React.MutableRefObject<HTMLButtonElement>;
  const pickerButtonRef = useRef<HTMLButtonElement>(null) as React.MutableRefObject<HTMLButtonElement>;

  const prevYearBtnRef = useRef<HTMLButtonElement>(null) as React.MutableRefObject<HTMLButtonElement>;
  const prevMonthBtnRef = useRef<HTMLButtonElement>(null) as React.MutableRefObject<HTMLButtonElement>;
  const nextMonthBtnRef = useRef<HTMLButtonElement>(null) as React.MutableRefObject<HTMLButtonElement>;
  const nextYearBtnRef = useRef<HTMLButtonElement>(null) as React.MutableRefObject<HTMLButtonElement>;
  const calendarDaysWrapperRef = useRef<HTMLDivElement>(null) as React.MutableRefObject<HTMLDivElement>;

  const prevHourBtnRef = useRef<HTMLButtonElement>(null) as React.MutableRefObject<HTMLButtonElement>;
  const currHourBtnRef = useRef<HTMLDivElement>(null) as React.MutableRefObject<HTMLDivElement>;
  const nextHourBtnRef = useRef<HTMLButtonElement>(null) as React.MutableRefObject<HTMLButtonElement>;
  const prevMinutesBtnRef = useRef<HTMLButtonElement>(null) as React.MutableRefObject<HTMLButtonElement>;
  const currMinutesBtnRef = useRef<HTMLDivElement>(null) as React.MutableRefObject<HTMLDivElement>;
  const nextMinutesBtnRef = useRef<HTMLButtonElement>(null) as React.MutableRefObject<HTMLButtonElement>;
  const prevMeridiemBtnRef = useRef<HTMLButtonElement>(null) as React.MutableRefObject<HTMLButtonElement>;
  const currMeridiemBtnRef = useRef<HTMLDivElement>(null) as React.MutableRefObject<HTMLDivElement>;
  const nextMeridiemBtnRef = useRef<HTMLButtonElement>(null) as React.MutableRefObject<HTMLButtonElement>;

  let firstSegmentRef = useRef<HTMLInputElement | HTMLButtonElement>(null) as React.MutableRefObject<
    HTMLInputElement | HTMLButtonElement
  >;
  if (isWithDateInputs) {
    firstSegmentRef = monthInputRef;
    if (firstSegment !== 'month-input') setFirstSegment('month-input');
  } else if (isWithTimeInputs) {
    firstSegmentRef = hourInputRef;
    if (firstSegment !== 'hour-input') setFirstSegment('hour-input');
  } else if (withAnyPicker) {
    firstSegmentRef = pickerButtonRef;
    if (isDateSelector && firstSegment !== 'date-picker-icon') setFirstSegment('date-picker-icon');
    if (isTimeSelector && firstSegment !== 'time-picker-icon') setFirstSegment('time-picker-icon');
    if (isDateTimeSelector && firstSegment !== 'datetime-picker-icon') setFirstSegment('datetime-picker-icon');
  }

  let lastSegmentRef = useRef<HTMLInputElement | HTMLButtonElement>(null) as React.MutableRefObject<
    HTMLInputElement | HTMLButtonElement
  >;
  if (withAnyPicker) {
    lastSegmentRef = pickerButtonRef;
    if (isDateSelector && lastSegment !== 'date-picker-icon') setLastSegment('date-picker-icon');
    if (isTimeSelector && lastSegment !== 'time-picker-icon') setLastSegment('time-picker-icon');
    if (isDateTimeSelector && lastSegment !== 'datetime-picker-icon') setLastSegment('datetime-picker-icon');
  } else if (withClearBtn) {
    lastSegmentRef = clearButtonRef;
    if (lastSegment !== 'clear-btn') setLastSegment('clear-btn');
  } else if (isWithTimeInputs) {
    lastSegmentRef = meridiemInputRef;
    if (lastSegment !== 'meridiem-input') setLastSegment('meridiem-input');
  } else if (isWithDateInputs) {
    lastSegmentRef = yearInputRef;
    if (lastSegment !== 'year-input') setLastSegment('year-input');
  }

  return useMemo(
    () => ({
      monthInputRef,
      dayInputRef,
      yearInputRef,
      hourInputRef,
      minutesInputRef,
      meridiemInputRef,
      clearButtonRef,
      pickerButtonRef,
      prevYearBtnRef,
      prevMonthBtnRef,
      nextMonthBtnRef,
      nextYearBtnRef,
      calendarDaysWrapperRef,
      prevHourBtnRef,
      currHourBtnRef,
      nextHourBtnRef,
      prevMinutesBtnRef,
      currMinutesBtnRef,
      nextMinutesBtnRef,
      prevMeridiemBtnRef,
      currMeridiemBtnRef,
      nextMeridiemBtnRef,
      firstSegmentRef,
      lastSegmentRef,
      firstSegment,
      lastSegment,
    }),
    [firstSegment, lastSegment],
  );
};
